import { ForeignLocalDataDecision, ForeignLocalDataReason } from '@wiztime/sync/config';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useServices } from '../../hooks/useServices';
import { exportAllData } from '../../lib/exportFile';
import { Button } from '../../ui/Button';
import { ConfirmDialog } from '../../ui/ConfirmDialog';
import styles from './Sync.module.css';
import { useForeignDataPrompt } from './foreignDataPrompt';

const Step = { Ask: 'ask', Merge: 'merge', ClearUnknown: 'clearUnknown' } as const;
type Step = (typeof Step)[keyof typeof Step];

/**
 * "This device holds data that is not from this account": asked when logging in, registering or joining a space.
 * Another space synced here before: clearing is the default; merging needs a typed confirmation because it makes
 * the old records visible in the new account. Origin unknown (records of other devices without a remembered
 * space, e.g. an imported backup): no default; uploading may be right, clearing needs a second confirmation
 * that offers an export first.
 */
export function ForeignDataDialog() {
  const question = useForeignDataPrompt((state) => state.question);
  const answer = useForeignDataPrompt((state) => state.answer);
  const { store, now } = useServices();
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>(Step.Ask);
  const [exportMessage, setExportMessage] = useState<{ text: string; failed: boolean } | null>(null);
  if (question === null) return null;
  const count = question.localRecords;

  const decide = (decision: ForeignLocalDataDecision): void => {
    setStep(Step.Ask);
    setExportMessage(null);
    answer(decision);
  };
  const back = (): void => {
    setStep(Step.Ask);
    setExportMessage(null);
  };
  const exportNow = (): void => {
    exportAllData(store, now()).then(
      ({ filename }) => setExportMessage({ text: t('sync.foreignData.exported', { name: filename }), failed: false }),
      (error: unknown) => {
        console.error('export before clearing failed', error);
        setExportMessage({ text: t('sync.foreignData.exportFailed'), failed: true });
      },
    );
  };

  if (step === Step.Merge) {
    return (
      <ConfirmDialog
        title={t('sync.foreignData.mergeTitle')}
        confirmLabel={t('sync.foreignData.mergeConfirm')}
        confirmWord={t('sync.foreignData.mergeWord')}
        cancelLabel={t('sync.foreignData.back')}
        danger
        onConfirm={() => decide(ForeignLocalDataDecision.Merge)}
        onCancel={back}
        testId="foreign-data-merge-dialog"
      >
        <p>{t('sync.foreignData.mergeBody', { count })}</p>
      </ConfirmDialog>
    );
  }

  if (step === Step.ClearUnknown) {
    return (
      <ConfirmDialog
        title={t('sync.foreignData.clearConfirmTitle')}
        confirmLabel={t('sync.foreignData.clearConfirmOk')}
        cancelLabel={t('sync.foreignData.back')}
        danger
        extraActions={
          <Button onClick={exportNow} data-testid="foreign-data-export">
            {t('sync.foreignData.clearConfirmExport')}
          </Button>
        }
        onConfirm={() => decide(ForeignLocalDataDecision.Clear)}
        onCancel={back}
        testId="foreign-data-clear-dialog"
      >
        <p>{t('sync.foreignData.clearConfirmBody', { count })}</p>
        {exportMessage === null ? null : (
          <p
            className={exportMessage.failed ? styles.errorText : styles.muted}
            role="status"
            data-testid="foreign-data-export-result"
          >
            {exportMessage.text}
          </p>
        )}
      </ConfirmDialog>
    );
  }

  if (question.reason === ForeignLocalDataReason.UnknownOrigin) {
    return (
      <ConfirmDialog
        title={t('sync.foreignData.unknownTitle')}
        cancelLabel={t('sync.foreignData.cancel')}
        extraActions={
          <>
            <Button onClick={() => decide(ForeignLocalDataDecision.Merge)} data-testid="foreign-data-upload">
              {t('sync.foreignData.upload')}
            </Button>
            <Button onClick={() => setStep(Step.ClearUnknown)} data-testid="foreign-data-clear">
              {t('sync.foreignData.clearUnknown')}
            </Button>
          </>
        }
        onConfirm={() => undefined}
        onCancel={() => decide(ForeignLocalDataDecision.Cancel)}
        testId="foreign-data-dialog"
      >
        <p>{t('sync.foreignData.unknownBody', { count })}</p>
      </ConfirmDialog>
    );
  }

  return (
    <ConfirmDialog
      title={t('sync.foreignData.title')}
      confirmLabel={t('sync.foreignData.clear')}
      cancelLabel={t('sync.foreignData.cancel')}
      extraActions={
        <Button onClick={() => setStep(Step.Merge)} data-testid="foreign-data-merge">
          {t('sync.foreignData.merge')}
        </Button>
      }
      onConfirm={() => decide(ForeignLocalDataDecision.Clear)}
      onCancel={() => decide(ForeignLocalDataDecision.Cancel)}
      testId="foreign-data-dialog"
    >
      <p>{t('sync.foreignData.body', { count })}</p>
    </ConfirmDialog>
  );
}
